import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import http, { apiErrorMessage } from '../Api/http';
import { GetGroups, GetCreatePermissions, FetchMessages } from '../Api/Api';
import toast from 'react-hot-toast';
import { selectUserId, selectUserTypeId, selectIsMasterAdmin, USER_TYPE } from '../redux/slices/authSlice';
import { card, BrandLoader } from '../components/uiKit';
import ConversationList from '../components/ChatComps/ConversationList';
import MessageThread from '../components/ChatComps/MessageThread';
import CreateGroupDialog from '../components/ChatComps/CreateGroupDialog';
import { normalizeGroup, previewTextFor, extractMessageList } from '../components/ChatComps/chatHelpers';
import {
    seedChatUnreadCounts, onChatGroupsChanged, onChatReconnected, onChatMessage, onChatTyping, setChatPageOpen,
} from '../components/ChatComps/chatSocket';

export default function ChatPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const myUserId = useSelector(selectUserId);
    const userTypeId = useSelector(selectUserTypeId);
    const isMasterAdmin = useSelector(selectIsMasterAdmin);
    const isEntityAdmin = userTypeId === USER_TYPE.ADMIN;

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [typingByGroupId, setTypingByGroupId] = useState({});
    // Default per the doc's §4.3 fallback: Admin allowed, everything else
    // (including MasterAdmin) not, unless GetCreatePermissions explicitly says
    // otherwise for the signed-in role. A previous version of this defaulted
    // MasterAdmin open too, to avoid locking out the app's own superuser
    // before any settings screen existed — but that just produced a confusing
    // flash (button appears, then the real permission check corrects it and
    // hides it a moment later). Now that "Group chat settings" (below) gives
    // MasterAdmin a real, permanent way to grant itself this permission, the
    // client no longer needs to guess — it can just show the correct answer
    // from the start.
    const [canCreateGroups, setCanCreateGroups] = useState(userTypeId === USER_TYPE.ADMIN);
    // Last-known-good preview per group, from messages we've actually seen
    // directly (via MessageThread's own FetchMessages, or a live
    // ReceiveMessage) — not React state, since it must survive re-renders
    // without itself triggering one; it's only ever read inside loadGroups.
    // Needed because GetGroups' own summary can come back with an empty
    // lastMessageText for a group even while lastMessageOn is correct (seen
    // in production), and loadGroups gets called very often — e.g. every
    // time MarkRead fires (MessageThread.jsx's mark-read-on-scroll effect
    // calls onGroupsListChanged on every new message, including your own)
    // — so a blind refetch was clobbering the live-patched preview from
    // onChatMessage/onLastMessageSeen below within moments of it being set.
    const previewOverridesRef = useRef(new Map());

    const setPreviewOverride = (groupId, msg) => {
        if (groupId == null || !msg) return;
        const text = previewTextFor(msg);
        if (!text) return;
        previewOverridesRef.current.set(groupId, {
            lastMessageText: text,
            lastMessageOn: msg.sentOn ?? null,
            lastMessageSenderName: msg.senderName ?? null,
        });
    };

    // Groups whose preview we've already requested directly via
    // fetchGroupPreview below — keeps loadGroups (which runs often, see
    // previewOverridesRef above) from re-firing the same FetchMessages call
    // every time it's re-invoked before the first one resolves.
    const previewFetchAttemptedRef = useRef(new Set());

    // The sidebar should show every chat's latest message by default, not
    // just the one the user has opened — but GetGroups' own summary can come
    // back with an empty lastMessageText even while lastMessageOn is correct
    // (see previewOverridesRef above). For any such group, fetch its real
    // latest message directly so the sidebar doesn't wait on the user to
    // click into that chat first. Returns the request's promise so the first
    // load (below) can hold its skeleton until every group's real preview is
    // in, and reveal the whole list at once — no per-row loading state.
    const fetchGroupPreview = useCallback((groupId) => {
        if (previewFetchAttemptedRef.current.has(groupId)) return Promise.resolve();
        previewFetchAttemptedRef.current.add(groupId);
        return http.get(FetchMessages, { params: { groupId, pageSize: 1 } })
            .then(({ data: body }) => {
                if (body?.error) return;
                const list = extractMessageList(body);
                const msg = list[list.length - 1];
                if (!msg) return;
                setPreviewOverride(groupId, msg);
                setGroups((cur) => cur.map((g) => (g.groupId === groupId ? {
                    ...g,
                    lastMessageText: previewTextFor(msg),
                    lastMessageOn: g.lastMessageOn ?? msg.sentOn ?? null,
                    lastMessageSenderName: g.lastMessageSenderName || msg.senderName || '',
                } : g)));
            })
            .catch(() => { previewFetchAttemptedRef.current.delete(groupId); });
    }, []);

    // loadGroups doesn't just run on mount — MessageThread calls it (as
    // onGroupsListChanged) after every send and every mark-read-on-scroll,
    // i.e. after nearly every message. Flashing the sidebar back to its
    // skeleton state on each of those background refreshes would hide the
    // list (and the message you just sent's own preview) behind a loading
    // spinner almost continuously — so only the very first load shows it.
    const hasLoadedOnceRef = useRef(false);

    const loadGroups = useCallback(() => {
        const isFirstLoad = !hasLoadedOnceRef.current;
        if (isFirstLoad) setLoading(true);
        return http.get(GetGroups)
            .then(({ data: body }) => {
                if (body?.error) throw new Error(body.message || 'Could not load your chats.');
                const list = Array.isArray(body?.data?.items) ? body.data.items : (Array.isArray(body?.data) ? body.data : []);
                const normalized = list.map(normalizeGroup).map((g) => {
                    const ov = previewOverridesRef.current.get(g.groupId);
                    // Only fill in what the backend left empty — never
                    // second-guess a real value it did send.
                    if (!ov || g.lastMessageText) return g;
                    return { ...g, lastMessageText: ov.lastMessageText, lastMessageOn: g.lastMessageOn ?? ov.lastMessageOn, lastMessageSenderName: g.lastMessageSenderName || ov.lastMessageSenderName };
                });
                setGroups(normalized);
                seedChatUnreadCounts(normalized);
                // lastMessageOn set but no text means a message exists that
                // GetGroups just didn't summarize — go fetch it. A group with
                // neither is genuinely empty ("No messages yet"), so leave it.
                const previewFetches = normalized
                    .filter((g) => g.lastMessageOn && !g.lastMessageText)
                    .map((g) => fetchGroupPreview(g.groupId));
                // Only the first load waits for these — the whole sidebar
                // stays on its skeleton until every row's real data (not
                // just GetGroups' own summary) is in, then reveals fully
                // populated in one go. Background refreshes already skip
                // the skeleton entirely (see hasLoadedOnceRef above), so
                // there previews are left to arrive on their own.
                if (isFirstLoad) return Promise.all(previewFetches);
            })
            .catch((err) => toast.error(apiErrorMessage(err, 'Could not load your chats.')))
            .finally(() => { hasLoadedOnceRef.current = true; setLoading(false); });
    }, [fetchGroupPreview]);

    useEffect(() => { loadGroups(); }, [loadGroups]);

    // Tells useChatSocket (mounted at DashboardLayout) that the hub
    // connection should be up — presence is tied 1:1 to this connection, so
    // leaving the chat page tears it down and the user shows offline.
    useEffect(() => {
        setChatPageOpen(true);
        return () => setChatPageOpen(false);
    }, []);

    // Doc §6 point 3: on hub reconnect, a plain GetGroups refresh is enough
    // for groups not currently open (previews/unread) — the currently-open
    // group's own catch-up is handled separately by MessageThread. Doc §5.2:
    // AddedToGroup / RemovedFromGroup / GroupDeleted / GroupUpdated all say
    // "add/remove from your local list (or re-fetch GetGroups)" — chatSocket
    // fans those out to onChatGroupsChanged so this list stays live even
    // while no specific group's thread is open.
    useEffect(() => {
        const unsubReconnect = onChatReconnected(loadGroups);
        const unsubGroupsChanged = onChatGroupsChanged(loadGroups);
        return () => { unsubReconnect(); unsubGroupsChanged(); };
    }, [loadGroups]);

    // Keeps the sidebar's last-message preview/time live — without this, a
    // new message only ever shows up in the list after the next full
    // GetGroups refetch (e.g. on reconnect), since ReceiveMessage otherwise
    // only feeds the unread badge (see chatSocket.js).
    useEffect(() => {
        const unsub = onChatMessage((msg) => {
            if (!msg || msg.groupId == null) return;
            setPreviewOverride(msg.groupId, msg);
            setGroups((list) => {
                if (!list.some((g) => g.groupId === msg.groupId)) return list;
                return list.map((g) => (g.groupId === msg.groupId ? {
                    ...g,
                    lastMessageText: previewTextFor(msg),
                    lastMessageOn: msg.sentOn ?? g.lastMessageOn,
                    lastMessageSenderName: msg.senderName ?? g.lastMessageSenderName,
                } : g));
            });
        });
        return unsub;
    }, []);

    // Sidebar "X is typing…" per group, live regardless of which group (if
    // any) is currently open.
    useEffect(() => {
        const unsub = onChatTyping((groupId, name) => {
            setTypingByGroupId((m) => {
                if (!name) {
                    if (!(groupId in m)) return m;
                    const next = { ...m };
                    delete next[groupId];
                    return next;
                }
                return { ...m, [groupId]: name };
            });
        });
        return unsub;
    }, []);

    useEffect(() => {
        // GetCreatePermissions lives under api/GroupChatSettings/..., which
        // the doc states 403s for every non-MasterAdmin caller ("Every
        // non-MasterAdmin request here 403s ... hide this screen entirely
        // for anyone else"). It's a MasterAdmin settings-management endpoint,
        // not a per-user "can I create" check, so only MasterAdmin calls it
        // here. Everyone else keeps the stated default (Admin allowed, else
        // not) and falls back to the doc's other suggested approach —
        // attempt CreateGroup and hide the button on a 403 — handled via
        // handleCreateForbidden below.
        if (!isMasterAdmin) return;
        http.get(GetCreatePermissions)
            .then(({ data: body }) => {
                if (body?.error) return;
                const rows = Array.isArray(body?.data) ? body.data : (Array.isArray(body?.data?.items) ? body.data.items : []);
                const mine = rows.find((r) => r.userTypeId === userTypeId);
                if (mine) setCanCreateGroups(Boolean(mine.canCreateGroups));
            })
            .catch(() => { /* keep the stated default on error */ });
    }, [userTypeId, isMasterAdmin]);

    const handleCreateForbidden = () => setCanCreateGroups(false);

    const selectedGroup = groups.find((g) => g.groupId === selectedGroupId) || null;

    const handleSelect = (groupId) => setSelectedGroupId(groupId);
    const handleBack = () => setSelectedGroupId(null);

    const handleLastMessageSeen = (groupId, msg) => {
        if (!msg) return;
        setPreviewOverride(groupId, msg);
        setGroups((list) => list.map((g) => (g.groupId === groupId ? {
            ...g,
            lastMessageText: previewTextFor(msg),
            lastMessageOn: msg.sentOn ?? g.lastMessageOn,
            lastMessageSenderName: msg.senderName ?? g.lastMessageSenderName,
        } : g)));
    };

    const handleGroupRemoved = (groupId) => {
        setGroups((list) => list.filter((g) => g.groupId !== groupId));
        setSelectedGroupId((cur) => (cur === groupId ? null : cur));
    };

    const handleCreated = (created) => {
        loadGroups().then(() => {
            const id = created?.groupId ?? created?.id;
            if (id != null) setSelectedGroupId(id);
        });
    };

    const showList = !isMobile || !selectedGroupId;
    const showThread = !isMobile || Boolean(selectedGroupId);

    return (
        <Box sx={{ p: 2, height: 'calc(100vh - 140px)', minHeight: 480 }}>
            <Box sx={{ ...card, height: '100%', display: 'flex', overflow: 'hidden' }}>
                <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, borderRight: { md: '1px solid #EEF0F6' }, display: showList ? 'block' : 'none' }}>
                    <ConversationList
                        groups={groups}
                        loading={loading}
                        selectedGroupId={selectedGroupId}
                        onSelect={handleSelect}
                        search={search}
                        onSearchChange={setSearch}
                        canCreateGroup={canCreateGroups}
                        onCreateGroup={() => setCreateOpen(true)}
                        isMasterAdmin={isMasterAdmin}
                        typingByGroupId={typingByGroupId}
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, display: showThread ? 'block' : 'none' }}>
                    {selectedGroupId ? (
                        <MessageThread
                            key={selectedGroupId}
                            groupId={selectedGroupId}
                            groupSummary={selectedGroup}
                            myUserId={myUserId}
                            isMasterAdmin={isMasterAdmin}
                            isEntityAdmin={isEntityAdmin}
                            isMobile={isMobile}
                            onBack={handleBack}
                            onGroupsListChanged={loadGroups}
                            onGroupRemoved={handleGroupRemoved}
                            onLastMessageSeen={handleLastMessageSeen}
                        />
                    ) : loading ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BrandLoader size={64} />
                        </Box>
                    ) : (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ textAlign: 'center', color: '#98A0AE' }}>
                                Select a conversation to start chatting
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            <CreateGroupDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} onForbidden={handleCreateForbidden} />
        </Box>
    );
}
